import axios from 'axios';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { isStringArray } from '@/api/queries/utils';
import { FilterState } from '@/types/filter';
import { unwrapS3Response, validateS3Url } from '@/utils/filter/unwrapS3Response';
import { isFilterState } from '@/utils/filter/isFilterState';
import { isLocalFiltersRecord } from '@/utils/filter/isLocalFiltersRecord';
import { isRecord } from '@/utils/filter/isRecord';
import { isOptionPathsByFilter } from '@/utils/filter/isOptionPathsByFilter';
import { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { migrateSharedViewKeys } from '@/utils/filter/filterKeyMigrations';

export interface FilterPayload {
  globalFilters: FilterState;
  localFilters: Record<string, Record<string, unknown>> | undefined;
  resolvedCountryCodes: string[] | undefined;
  senderCountryCodes: string[] | undefined;
  /**
   * Option paths the hierarchical filters were selected against. Carried in the
   * payload for the same reason as `resolvedCountryCodes`: when a saved filter or
   * shared view is applied, the option queries that would produce them have not
   * run, and without them those filters fall back to different query semantics.
   */
  optionPaths: OptionPathsByFilter | undefined;
}

const fetchFilterPayload = async (filterId: string): Promise<FilterPayload> => {
  const detailResponse = await UserFilterApiService.getFilterById(filterId);
  const { filters_download_link: downloadLink } = detailResponse.data;
  validateS3Url(downloadLink);

  const filtersResponse = await axios.get<Record<string, unknown>>(downloadLink);
  const unwrappedRaw = unwrapS3Response(filtersResponse.data);

  // Migrate legacy global-filter keys: renamed keys are mapped to their
  // current FilterType value and removed keys are dropped, so filters saved
  // by older app versions still load instead of silently losing values.
  const migrated = migrateSharedViewKeys(unwrappedRaw);
  const unwrapped = isRecord(migrated) ? migrated : unwrappedRaw;

  const hasStructuredPayload = 'globalFilters' in unwrapped && isFilterState(unwrapped.globalFilters);

  let globalFilters: FilterState;
  if (hasStructuredPayload && isFilterState(unwrapped.globalFilters)) {
    globalFilters = unwrapped.globalFilters;
  } else if (isFilterState(unwrapped)) {
    globalFilters = unwrapped;
  } else {
    throw new Error('Invalid filter payload');
  }

  const localFilters =
    hasStructuredPayload && isLocalFiltersRecord(unwrapped.localFilters) ? unwrapped.localFilters : undefined;

  const resolvedCountryCodes = isStringArray(unwrapped.resolvedCountryCodes)
    ? unwrapped.resolvedCountryCodes
    : undefined;
  const senderCountryCodes = isStringArray(unwrapped.senderCountryCodes) ? unwrapped.senderCountryCodes : undefined;
  const optionPaths = isOptionPathsByFilter(unwrapped.optionPaths) ? unwrapped.optionPaths : undefined;

  return { globalFilters, localFilters, resolvedCountryCodes, senderCountryCodes, optionPaths };
};

export default fetchFilterPayload;
